import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useAddVideoGuide, parseVimeoId } from "@/hooks/use-video-guides";

interface VideoGuideAddModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const VideoGuideAddModal: React.FC<VideoGuideAddModalProps> = ({ open, onOpenChange }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [vimeoUrl, setVimeoUrl] = useState("");
  const addMutation = useAddVideoGuide();

  const vimeoId = parseVimeoId(vimeoUrl);
  const urlValid = vimeoUrl.length === 0 || !!vimeoId;

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setVimeoUrl("");
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !vimeoId) return;
    addMutation.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        vimeoUrl: vimeoUrl.trim(),
      },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Video</DialogTitle>
          <DialogDescription>
            Paste a Vimeo URL (e.g. https://vimeo.com/123456789). The video will play inline in the
            Help section.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="video-title">Title *</Label>
            <Input
              id="video-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter video title"
              required
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="video-description">Description</Label>
            <Textarea
              id="video-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
              maxLength={1000}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="video-url">Vimeo URL *</Label>
            <Input
              id="video-url"
              value={vimeoUrl}
              onChange={(e) => setVimeoUrl(e.target.value)}
              placeholder="https://vimeo.com/123456789"
              required
              aria-invalid={!urlValid}
            />
            {!urlValid && (
              <p className="text-xs text-destructive">
                That doesn't look like a valid Vimeo URL.
              </p>
            )}
          </div>

          {vimeoId && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <AspectRatio ratio={16 / 9} className="bg-muted rounded overflow-hidden">
                <iframe
                  src={`https://player.vimeo.com/video/${vimeoId}`}
                  title="Vimeo preview"
                  className="w-full h-full"
                  frameBorder={0}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </AspectRatio>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || !vimeoId || addMutation.isPending}
            >
              {addMutation.isPending ? "Adding..." : "Add Video"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};